import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useIntl } from 'react-intl';
import Table from '../../components/Table';
import { ITableColumns } from '../../models';
import { MUIDataTableMeta, MUIDataTableOptions, MUIDataTableState } from 'mui-datatables';
import { useListRetrieves } from '../../hooks/use-list-retrieves';
import { RetrieveTableView } from '@tff/types/tff/retrieve-flight-service';
import { Status } from '@tff/types/tff/retrieve';
import TFFSelectInput from '../../styled-components/TFFSelectInput';

const DEFAULT_LIMIT = 50;
const STATUS_OPTIONS: Status[] = ['BOOKED', 'PENDING', 'FAILED', 'CANCELLED'];

const BookingLinkCell = ({ value, bookingSource }: { value: string | number; bookingSource: string }) => (
  <Link to={`/tff/order/${value}@${bookingSource}`}>{value}</Link>
);

const TicketedCell = ({ value }: { value: string | number | boolean }) => {
  if (value === undefined) return <></>;
  return <div style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>{value ? '✅' : '❌'}</div>;
};

const TextCell = ({ value, center }: { value: string | number; center?: boolean }) => (
  <div style={{ whiteSpace: 'nowrap', ...(center && { textAlign: 'center' as const }) }}>{value}</div>
);

const DateCell = ({ value }: { value: string | number }) => (
  <div style={{ whiteSpace: 'nowrap' }}>
    {value && value !== '' && format(new Date(value), 'dd.MM.yyyy HH:mm')}
  </div>
);

const BOOKING_SOURCE_COLUMN_INDEX = 8;
const renderBookingLink = (value: string | number, tableMeta: MUIDataTableMeta) => (
  <BookingLinkCell value={value} bookingSource={tableMeta.rowData[BOOKING_SOURCE_COLUMN_INDEX] as string} />
);

const renderTicketed = (value: string | number | boolean) => <TicketedCell value={value} />;

const createTextColumn = (intl: any, name: string, labelId: string, centered = false) => ({
  name,
  label: intl.formatMessage({ id: labelId }),
  options: {
    display: true,
    filter: true,
    filterList: [] as [] | [string],
    customBodyRender: (value: string | number) => <TextCell value={value} center={centered} />,
  },
});

const createDateColumn = (intl: any, name: string, labelId: string) => ({
  name,
  label: intl.formatMessage({ id: labelId }),
  options: {
    display: true,
    filter: true,
    filterList: [] as [] | [string],
    customBodyRender: (value: string | number) => <DateCell value={value} />,
  },
});

const RetrieveList = () => {
  const intl = useIntl();
  const { data, fetchRetrieves, clearCache } = useListRetrieves();
  const nextCursorRef = useRef<string | undefined>();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_LIMIT);
  const [status, setStatus] = useState<Status>('BOOKED');
  const [total, setTotal] = useState(0);
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([undefined]);
  const [filters, setFilters] = useState<Record<string, string>>({});

  useEffect(() => {
    nextCursorRef.current = data.nextCursor;
    if (data.total > 0) {
      setTotal(data.total);
    }
  }, [data]);

  useEffect(() => {
    fetchRetrieves({ limit: rowsPerPage, status });
  }, []);

  const handleStatusChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const newStatus = event.target.value as Status;
    setStatus(newStatus);
    setPage(0);
    setTotal(0);
    setCursorHistory([undefined]);
    clearCache();
    fetchRetrieves({ limit: rowsPerPage, status: newStatus });
  };

  const handleTableChange = (action: string, tableState: MUIDataTableState) => {
    if (action === 'changePage') {
      const newPage = tableState.page;

      if (newPage > page && nextCursorRef.current) {
        setCursorHistory(prev => [...prev.slice(0, newPage), nextCursorRef.current]);
        fetchRetrieves({ limit: rowsPerPage, status, cursor: nextCursorRef.current });
      } else {
        fetchRetrieves({ limit: rowsPerPage, status, cursor: cursorHistory[newPage] });
      }

      setPage(newPage);
    }

    if (action === 'changeRowsPerPage') {
      const newRowsPerPage = tableState.rowsPerPage;
      setRowsPerPage(newRowsPerPage);
      setPage(0);
      setCursorHistory([undefined]);
      clearCache();
      fetchRetrieves({ limit: newRowsPerPage, status });
    }
  };

  const getNestedValue = (obj: any, path: string): any =>
    path.split('.').reduce((acc, key) => acc?.[key], obj);

  const filteredItems = data.items.filter(item =>
    Object.entries(filters).every(([key, filterValue]) => {
      const cellValue = String(getNestedValue(item, key) ?? '');
      return cellValue.toLowerCase().includes(filterValue.toLowerCase());
    }),
  );

  const handleFilterChange = (columnName: string, filterValue: string | undefined) => {
    setFilters(prev => {
      if (!filterValue) {
        const { [columnName]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [columnName]: String(filterValue) };
    });
  };

  const tableOptions: MUIDataTableOptions = {
    serverSide: true,
    page,
    rowsPerPage,
    rowsPerPageOptions: [25, 50, 100],
    count: total,
    onTableChange: handleTableChange,
    sortOrder: { name: 'createdAt', direction: 'desc' },
    onFilterChange: (changedColumn, filterList, _type, changedColumnIndex) => {
      const value = filterList[changedColumnIndex]?.[0];
      handleFilterChange(changedColumn as string, value);
    },
  };

  const columnsData: ITableColumns[] = [
    {
      name: 'no',
      label: intl.formatMessage({ id: 'pages.retrieveList.list.no' }),
      options: {
        display: true,
        filter: false,
        filterList: [] as [] | [string],
        customBodyRender: (_value: string | number, tableMeta: MUIDataTableMeta) =>
          `${page * rowsPerPage + tableMeta.rowIndex + 1}`,
        setCellProps: () => ({
          style: { whiteSpace: 'nowrap', position: 'sticky', left: '0', background: 'white', zIndex: 100 },
        }),
        setCellHeaderProps: () => ({
          style: { whiteSpace: 'nowrap', position: 'sticky', left: '0', background: 'white', zIndex: 101 },
        }),
      },
    },
    createTextColumn(intl, 'product', 'pages.retrieveList.list.product'),
    createTextColumn(intl, 'channel', 'pages.retrieveList.list.channel'),
    createTextColumn(intl, 'tfmPnr', 'pages.retrieveList.list.tfmPnr'),
    createTextColumn(intl, 'status', 'pages.retrieveList.list.status'),
    {
      name: 'bookingResponse.recordLocator',
      label: intl.formatMessage({ id: 'pages.retrieveList.list.bookingid' }),
      options: {
        display: true,
        filter: true,
        filterList: [] as [] | [string],
        customBodyRender: renderBookingLink,
      },
    },
    createTextColumn(intl, 'orderId', 'pages.retrieveList.list.orderId'),
    createTextColumn(intl, 'journeyType', 'pages.retrieveList.list.journeyType'),
    createTextColumn(intl, 'bookingSource', 'pages.retrieveList.list.bookingsrc', true),
    createTextColumn(intl, 'sourceInfo.targetEnvironment', 'pages.retrieveList.list.environment', true),
    createTextColumn(intl, 'midocoOrderId', 'pages.retrieveList.list.midocoOrderId'),
    createTextColumn(intl, 'midocoOrderNo', 'pages.retrieveList.list.midocoOrderNo'),
    createTextColumn(intl, 'clientPnr', 'pages.retrieveList.list.clientPnr'),
    createDateColumn(intl, 'bookingResponse.bookingDate', 'pages.retrieveList.list.bookingDate'),
    {
      name: 'ticketed',
      label: intl.formatMessage({ id: 'pages.retrieveList.list.ticketed' }),
      options: {
        display: true,
        filter: true,
        filterList: [] as [] | [string],
        customBodyRender: renderTicketed,
      },
    },
    createDateColumn(intl, 'ticketTimeLimit', 'pages.retrieveList.list.ticketTimeLimit'),
    createTextColumn(intl, 'origin', 'pages.retrieveList.list.origin', true),
    createTextColumn(intl, 'destination', 'pages.retrieveList.list.destination', true),
    createDateColumn(intl, 'outDeparture', 'pages.retrieveList.list.outDeparture'),
    createTextColumn(intl, 'outBookingClasses', 'pages.retrieveList.list.outBookingClasses', true),
    createTextColumn(intl, 'outCabinClasses', 'pages.retrieveList.list.outCabinClasses', true),
    createDateColumn(intl, 'returnDeparture', 'pages.retrieveList.list.inDeparture'),
    createTextColumn(intl, 'returnBookingClasses', 'pages.retrieveList.list.inBookingClasses', true),
    createTextColumn(intl, 'returnCabinClasses', 'pages.retrieveList.list.inCabinClasses', true),
    createTextColumn(intl, 'bookingSessionId', 'pages.retrieveList.list.bookingSessId'),
    createDateColumn(intl, 'createdAt', 'pages.retrieveList.list.createdAt'),
    createDateColumn(intl, 'updatedAt', 'pages.retrieveList.list.updatedAt'),
    {
      name: 'version',
      label: 'Version',
      options: { display: true, filter: true, filterList: [] as [] | [string] },
    },
  ];

  return (
    <>
      <div style={{ marginBottom: '16px', maxWidth: '200px' }}>
        <TFFSelectInput id="retrieve-status-filter" label="Status" value={status} onChange={handleStatusChange}>
          {STATUS_OPTIONS.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </TFFSelectInput>
      </div>
      <Table
        data={filteredItems as RetrieveTableView[]}
        columnsData={columnsData}
        tableId="retrieveDetailsTable"
        otherOptions={tableOptions}
        tableName={`${intl.formatMessage({ id: 'pages.retrieveList.list.headerStart' })} ${total} ${intl.formatMessage({ id: 'pages.retrieveList.list.headerEnd' })}`}
      />
    </>
  );
};

export default RetrieveList;
